import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { getUnreplacedPromoPlaceholders } from '@/utils/sms/placeholderUtils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from '@/components/ui/tabs';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  ChevronLeft,
  ChevronRight,
  Check,
  AlertCircle,
  Info,
  Clock,
  MessageSquare,
  Target,
  FileText,
  Eye,
  CalendarIcon
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/context/auth';
import { useTriggersManagement, useFetchTriggers } from '@/hooks/sms/useTriggers';
import TriggerConditionBuilder from './TriggerConditionBuilder';
import MessageComposition from '@/components/sms/campaign/MessageComposition';
import PromoCodeSelector from '@/components/sms/promocodes/PromoCodeSelector';
import EnhancedMessageCharacterCount from '@/components/sms/shared/counters/EnhancedMessageCharacterCount';
import {
  TriggerFormData,
  TriggerFormStep,
  TriggerType,
  TriggerTemplate,
  TriggerConditionFormData,
  ConditionField,
  ConditionOperator,
  TimeUnit,
  TRIGGER_TYPE_LABELS,
  TRIGGER_TYPE_DESCRIPTIONS,
  TIME_UNIT_LABELS,
  SmsTriggerRule
} from '@/types/sms-triggers';
import { formatTimeDelay, getTimingMessage } from '@/utils/sms/time-formatting';

interface TriggerFormProps {
  triggerId?: string | null;
  onSuccess: () => void;
  onCancel: () => void;
}

const FORM_STEPS: { id: TriggerFormStep; label: string; icon: React.ReactNode }[] = [
  { id: 'basic', label: 'Basic Info', icon: <FileText className="h-4 w-4" /> },
  { id: 'conditions', label: 'Conditions', icon: <Target className="h-4 w-4" /> },
  { id: 'message', label: 'Message', icon: <MessageSquare className="h-4 w-4" /> },
  { id: 'timing', label: 'Timing', icon: <Clock className="h-4 w-4" /> },
  { id: 'review', label: 'Review', icon: <Eye className="h-4 w-4" /> }
];

const TriggerForm: React.FC<TriggerFormProps> = ({
  triggerId,
  onSuccess,
  onCancel
}) => {
  const { user } = useAuth();
  const { createTrigger, updateTrigger, loading: submitting } = useTriggersManagement(user);
  const { triggers } = useFetchTriggers(user);
  
  const [currentStep, setCurrentStep] = useState<TriggerFormStep>('basic');
  const [completedSteps, setCompletedSteps] = useState<Set<TriggerFormStep>>(new Set());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState<TriggerFormData>({
    rule_name: '',
    rule_description: '',
    trigger_type: 'milestone',
    conditions: [],
    logic: 'AND',
    time_delay: { value: 0, unit: 'minutes' },
    message_template: '',
    personalization_fields: [],
    priority: 100,
    max_sends_per_customer: 1,
    cooldown_days: undefined,
    is_active: false
  });

  // Handle trigger type changes - add/remove days_since_last_order condition as needed
  useEffect(() => {
    if (formData.trigger_type === 're_engagement') {
      // Find existing days_since_last_order condition
      const existingDaysCondition = formData.conditions.find(c => c.field === 'days_since_last_order');
      const otherConditions = formData.conditions.filter(c => c.field !== 'days_since_last_order');

      if (existingDaysCondition) {
        // If it exists (e.g., from a template), ensure it's first and marked as required
        // Preserve the existing operator and value from the template
        const updatedDaysCondition = {
          ...existingDaysCondition,
          isRequired: true
        };

        // Only update if it's not already the first condition or not marked as required
        if (formData.conditions[0]?.field !== 'days_since_last_order' || !formData.conditions[0]?.isRequired) {
          setFormData(prev => ({
            ...prev,
            conditions: [updatedDaysCondition, ...otherConditions]
          }));
        }
      } else {
        // If it doesn't exist, add default days_since_last_order condition
        const newConditions = [{
          id: `condition_${Date.now()}`,
          field: 'days_since_last_order' as ConditionField,
          operator: 'greater_than' as ConditionOperator,  // Changed from 'equals' to 'greater_than'
          value: 30,
          field_type: 'number' as const,
          isRequired: true
        }, ...otherConditions];
        setFormData(prev => ({ ...prev, conditions: newConditions }));
      }

      // Force AND logic for re_engagement triggers
      if (formData.logic !== 'AND') {
        setFormData(prev => ({ ...prev, logic: 'AND' }));
      }
    } else {
      // When switching away from re_engagement, remove the required days_since_last_order condition
      // Only remove if it was marked as required (i.e., automatically added)
      const hasRequiredDaysCondition = formData.conditions.some(c =>
        c.field === 'days_since_last_order' && c.isRequired === true
      );

      if (hasRequiredDaysCondition) {
        // Remove the required days_since_last_order condition
        const filteredConditions = formData.conditions.filter(c =>
          !(c.field === 'days_since_last_order' && c.isRequired === true)
        );

        // Only update if we actually removed something
        if (filteredConditions.length !== formData.conditions.length) {
          setFormData(prev => ({
            ...prev,
            conditions: filteredConditions
          }));
        }
      }
    }
  }, [formData.trigger_type]);

  const [startDate, setStartDate] = useState<Date | undefined>(
    formData.starts_at ? new Date(formData.starts_at) : undefined
  );
  const [endDate, setEndDate] = useState<Date | undefined>(
    formData.expires_at ? new Date(formData.expires_at) : undefined
  );
  const [testPhoneNumber, setTestPhoneNumber] = useState<string>('');

  // Load template data if available
  useEffect(() => {
    const templateData = localStorage.getItem('selectedTriggerTemplate');
    if (templateData) {
      try {
        const template: TriggerTemplate = JSON.parse(templateData);
        setFormData(prev => ({
          ...prev,
          rule_name: template.name,
          rule_description: template.description,
          trigger_type: template.trigger_type,
          conditions: template.default_conditions,
          logic: template.default_logic,
          time_delay: template.default_time_delay,
          message_template: template.default_message,
          personalization_fields: template.personalization_fields,
          priority: template.default_priority ?? prev.priority,
          max_sends_per_customer: template.default_max_sends_per_customer !== undefined
            ? template.default_max_sends_per_customer
            : prev.max_sends_per_customer,
          cooldown_days: template.default_cooldown_days !== undefined
            ? template.default_cooldown_days
            : prev.cooldown_days
        }));
        localStorage.removeItem('selectedTriggerTemplate');
      } catch (error) {
        console.error('Error loading template:', error);
      }
    }
  }, []);

  // Load existing trigger data for editing
  useEffect(() => {
    if (triggerId && triggers.length > 0) {
      const trigger = triggers.find(t => t.id === triggerId);
      if (trigger) {
        loadTriggerData(trigger);
      }
    }
  }, [triggerId, triggers]);

  // Sync start date with formData
  useEffect(() => {
    if (startDate) {
      updateFormData({ starts_at: startDate.toISOString() });
    } else {
      updateFormData({ starts_at: undefined });
    }
  }, [startDate]);

  // Sync end date with formData
  useEffect(() => {
    if (endDate) {
      updateFormData({ expires_at: endDate.toISOString() });
    } else {
      updateFormData({ expires_at: undefined });
    }
  }, [endDate]);

  const mapDatabaseTypeToFormType = (dbType: string): TriggerType => {
    // Map database trigger types to simplified form types
    switch (dbType) {
      case 'customer_activity_update':
      case 'order_confirmed':
      case 'nth_order':
      case 'first_order':
      case 'spending_milestone':
        return 'milestone';
      case 'customer_inactive':
        return 're_engagement';
      default:
        // If it's already a form type, return it as is
        return dbType as TriggerType;
    }
  };

  const loadTriggerData = (trigger: SmsTriggerRule) => {
    // Convert database format to form format
    const conditions: TriggerConditionFormData[] = Object.entries(trigger.rule_conditions.conditions || {}).map(
      ([field, condition], index) => ({
        id: `condition_${index}`,
        field: field as any,
        operator: condition.operator,
        value: condition.value,
        field_type: condition.field_type || 'string'
      })
    );

    // Map database trigger type to form trigger type
    const formTriggerType = mapDatabaseTypeToFormType(trigger.trigger_type as string);

    setFormData({
      rule_name: trigger.rule_name,
      rule_description: trigger.rule_description || '',
      trigger_type: formTriggerType,
      conditions,
      logic: trigger.rule_conditions.logic,
      time_delay: trigger.time_delay,
      send_time_preference: trigger.send_time_preference,
      message_template: trigger.message_template,
      personalization_fields: trigger.personalization_fields || [],
      priority: trigger.priority,
      max_sends_per_customer: trigger.max_sends_per_customer,
      cooldown_days: trigger.cooldown_days,
      starts_at: trigger.starts_at,
      expires_at: trigger.expires_at,
      is_active: trigger.is_active
    });

    // Set date states
    setStartDate(trigger.starts_at ? new Date(trigger.starts_at) : undefined);
    setEndDate(trigger.expires_at ? new Date(trigger.expires_at) : undefined);
  };

  const validateStep = (step: TriggerFormStep): boolean => {
    const newErrors: Record<string, string> = {};

    switch (step) {
      case 'basic':
        if (!formData.rule_name.trim()) {
          newErrors.rule_name = 'Trigger name is required';
        }
        if (!formData.trigger_type) {
          newErrors.trigger_type = 'Trigger type is required';
        }
        break;

      case 'conditions':
        if (formData.conditions.length === 0) {
          newErrors.conditions = 'At least one condition is required';
        }
        formData.conditions.forEach((condition, index) => {
          if (!condition.value && condition.operator !== 'in' && condition.operator !== 'not_in') {
            newErrors[`condition_${index}`] = 'Value is required';
          }
          if ((condition.operator === 'in' || condition.operator === 'not_in') && 
              (!Array.isArray(condition.value) || condition.value.length === 0)) {
            newErrors[`condition_${index}`] = 'At least one value must be selected';
          }
        });
        break;

      case 'message':
        if (!formData.message_template.trim()) {
          newErrors.message_template = 'Message content is required';
        }

        // Check for unreplaced manual placeholders
        const unreplacedPlaceholders = getUnreplacedPromoPlaceholders(formData.message_template);

        if (unreplacedPlaceholders.length > 0) {
          newErrors.message_template = `Please replace the following placeholders before continuing: ${unreplacedPlaceholders.join(', ')}`;
        }
        break;

      case 'timing':
        if (formData.time_delay.value < 0) {
          newErrors.time_delay = 'Time delay cannot be negative';
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      // Mark current step as completed
      setCompletedSteps(prev => new Set([...prev, currentStep]));
      
      const currentIndex = FORM_STEPS.findIndex(step => step.id === currentStep);
      if (currentIndex < FORM_STEPS.length - 1) {
        setCurrentStep(FORM_STEPS[currentIndex + 1].id);
      }
    }
  };

  const handlePrevious = () => {
    const currentIndex = FORM_STEPS.findIndex(step => step.id === currentStep);
    if (currentIndex > 0) {
      setCurrentStep(FORM_STEPS[currentIndex - 1].id);
    }
  };

  const handleSubmit = async () => {
    // Validate all steps
    const allStepsValid = FORM_STEPS.every(step => validateStep(step.id));
    
    if (!allStepsValid) {
      toast({
        title: 'Validation Error',
        description: 'Please fix the errors before submitting',
        variant: 'destructive'
      });
      return;
    }

    try {
      // Prepare submission data
      let submissionData = { ...formData };
      
      // For re_engagement triggers, convert hours to minutes if needed
      if (formData.trigger_type === 're_engagement' && formData.time_delay.unit === 'hours') {
        submissionData.time_delay = {
          value: formData.time_delay.value * 60,
          unit: 'minutes'
        };
      }
      
      if (triggerId) {
        await updateTrigger(triggerId, submissionData);
      } else {
        await createTrigger(submissionData);
      }
      onSuccess();
    } catch (error) {
      console.error('Error saving trigger:', error);
    }
  };

  const updateFormData = (updates: Partial<TriggerFormData>) => {
    setFormData(prev => ({ ...prev, ...updates }));
  };

  const getStepStatus = (stepId: TriggerFormStep) => {
    if (completedSteps.has(stepId)) return 'completed';
    if (stepId === currentStep) return 'current';
    return 'upcoming';
  };

  const isStepClickable = (stepId: TriggerFormStep) => {
    // Step is clickable if it's completed
    if (completedSteps.has(stepId)) return true;
    
    // Step is clickable if it's the current step
    if (stepId === currentStep) return true;
    
    // Step is clickable if it's immediately after the last completed step
    const stepIndex = FORM_STEPS.findIndex(step => step.id === stepId);
    if (stepIndex > 0) {
      const previousStep = FORM_STEPS[stepIndex - 1];
      if (completedSteps.has(previousStep.id)) {
        // Check if this is the first uncompleted step after completed ones
        const allPreviousCompleted = FORM_STEPS.slice(0, stepIndex).every(
          step => completedSteps.has(step.id)
        );
        return allPreviousCompleted;
      }
    }
    
    return false;
  };


  return (
    <div className="w-full max-w-full mx-auto space-y-6">
      {/* Progress Steps */}
      <Card>
        <CardContent className="p-4 sm:p-6">
          <div className="flex items-center justify-between gap-2 overflow-x-auto scrollbar-hide">
            {FORM_STEPS.map((step, index) => {
              const status = getStepStatus(step.id);
              const isClickable = isStepClickable(step.id);
              return (
                <div key={step.id} className="flex items-center">
                  <button
                    type="button"
                    onClick={() => isClickable && setCurrentStep(step.id)}
                    disabled={!isClickable}
                    className={`flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 transition-colors flex-shrink-0 ${
                      status === 'completed'
                        ? 'bg-green-600 border-green-600 text-white hover:bg-green-700'
                        : status === 'current'
                        ? 'border-primary text-primary'
                        : 'border-muted-foreground text-muted-foreground'
                    } ${isClickable ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                  >
                    {status === 'completed' ? (
                      <Check className="h-4 w-4 sm:h-5 sm:w-5 stroke-white" />
                    ) : (
                      <div className="h-4 w-4 sm:h-5 sm:w-5 flex items-center justify-center">
                        {step.icon}
                      </div>
                    )}
                  </button>
                  <div className="ml-3 hidden sm:block">
                    <p className={`text-sm font-medium ${
                      status === 'current' ? 'text-primary' : status === 'completed' ? 'text-foreground' : 'text-muted-foreground'
                    }`}>
                      {step.label}
                    </p>
                  </div>
                  {index < FORM_STEPS.length - 1 && (
                    <div className={`w-8 sm:w-12 h-0.5 mx-2 sm:mx-4 flex-shrink-0 ${
                      completedSteps.has(FORM_STEPS[index + 1].id)
                        ? 'bg-green-600'
                        : 'bg-border'
                    }`} />
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Form Content */}
      <Card>
        <CardContent className="p-4 sm:p-6">
          {currentStep === 'basic' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium mb-2">Basic Information</h3>
                <p className="text-sm text-muted-foreground mb-6">
                  Set up the basic details for your trigger
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="rule_name">Trigger Name *</Label>
                  <Input
                    id="rule_name"
                    value={formData.rule_name}
                    onChange={(e) => updateFormData({ rule_name: e.target.value })}
                    placeholder="e.g., Welcome New Customers"
                  />
                  {errors.rule_name && (
                    <p className="text-sm text-destructive">{errors.rule_name}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="trigger_type">Trigger Type *</Label>
                  <Select
                    value={formData.trigger_type}
                    onValueChange={(value) => updateFormData({ trigger_type: value as TriggerType })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select trigger type" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(TRIGGER_TYPE_LABELS).map(([value, label]) => (
                        <SelectItem key={value} value={value}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.trigger_type && (
                    <p className="text-sm text-destructive">{errors.trigger_type}</p>
                  )}
                  {formData.trigger_type && TRIGGER_TYPE_DESCRIPTIONS[formData.trigger_type] && (
                    <p className="text-sm text-muted-foreground mt-1">
                      {TRIGGER_TYPE_DESCRIPTIONS[formData.trigger_type]}
                    </p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="rule_description">Description</Label>
                <Textarea
                  id="rule_description"
                  value={formData.rule_description}
                  onChange={(e) => updateFormData({ rule_description: e.target.value })}
                  placeholder="Describe what this trigger does and when it should be used"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="priority">Priority</Label>
                  <Input
                    id="priority"
                    type="number"
                    value={formData.priority}
                    onChange={(e) => updateFormData({ priority: parseInt(e.target.value) || 100 })}
                    placeholder="100"
                    min="1"
                    max="1000"
                  />
                  <p className="text-xs text-muted-foreground">
                    Lower numbers = higher priority
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="max_sends">Max Sends per Customer</Label>
                  <Input
                    id="max_sends"
                    type="number"
                    value={formData.max_sends_per_customer || ''}
                    onChange={(e) => updateFormData({ 
                      max_sends_per_customer: e.target.value ? parseInt(e.target.value) : undefined 
                    })}
                    placeholder="No limit"
                    min="1"
                  />
                  <p className="text-xs text-muted-foreground">
                    Leave empty for no limit
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cooldown">Cooldown (days)</Label>
                  <Input
                    id="cooldown"
                    type="number"
                    value={formData.cooldown_days || ''}
                    onChange={(e) => updateFormData({ 
                      cooldown_days: e.target.value ? parseInt(e.target.value) : undefined 
                    })}
                    placeholder="No cooldown"
                    min="1"
                  />
                  <p className="text-xs text-muted-foreground">
                    Wait period between triggers
                  </p>
                </div>
              </div>
            </div>
          )}

          {currentStep === 'conditions' && (
            <TriggerConditionBuilder
              conditions={formData.conditions}
              logic={formData.logic}
              onChange={(conditions, logic) => updateFormData({ conditions, logic })}
              errors={errors}
              triggerType={formData.trigger_type}
            />
          )}

          {currentStep === 'message' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium mb-2">Message Content</h3>
                <p className="text-sm text-muted-foreground mb-6">
                  Create the message that will be sent when the trigger activates. 
                  Use dynamic variables to personalize messages with customer information 
                  and restaurant-specific links.
                </p>
              </div>

              {/* Show validation error if present */}
              {errors.message_template && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{errors.message_template}</AlertDescription>
                </Alert>
              )}

              <MessageComposition
                campaignData={{
                  message: formData.message_template,
                  testPhoneNumber: testPhoneNumber
                }}
                updateCampaignData={(data: any) => {
                  if (data.message !== undefined) {
                    updateFormData({ message_template: data.message });
                  }
                  if (data.testPhoneNumber !== undefined) {
                    setTestPhoneNumber(data.testPhoneNumber);
                  }
                }}
                userRestaurants={user?.restaurants || []}
                allowDynamicVariables={true}
                hidePromoCode={false}  // Enable promo code selector
              />

              {/* Alerts for manual placeholders */}
              {formData.message_template.includes('[CODE]') && (
                <Alert className="mt-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Your message contains a <strong>[CODE]</strong> placeholder.
                    Please use the Promo Code selector above to replace it with an actual promo code,
                    or manually replace [CODE] with your desired code.
                  </AlertDescription>
                </Alert>
              )}

              {formData.message_template.includes('[ITEM]') && (
                <Alert className="mt-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Your message contains an <strong>[ITEM]</strong> placeholder.
                    Please replace [ITEM] with the actual item name you want to offer.
                  </AlertDescription>
                </Alert>
              )}

              {formData.message_template.includes('[DISCOUNT]') && (
                <Alert className="mt-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Your message contains a <strong>[DISCOUNT]</strong> placeholder.
                    Please replace [DISCOUNT] with the actual discount amount (e.g., "20%" or "$5").
                  </AlertDescription>
                </Alert>
              )}

              {/* Help text for dynamic variables */}
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  <strong>Dynamic Restaurant URLs:</strong> When you select "Dynamic Variables" from the restaurant dropdown, 
                  the URLs will automatically resolve to the customer's most recently ordered restaurant 
                  within your organisation. If URLs are missing, the system will use organisation defaults or the trigger's restaurant as fallbacks.
                </AlertDescription>
              </Alert>
            </div>
          )}

          {currentStep === 'timing' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium mb-2">Timing & Scheduling</h3>
                <p className="text-sm text-muted-foreground mb-6">
                  {formData.trigger_type === 're_engagement' 
                    ? 'Configure when the message should be sent before the customer\'s typical order time'
                    : 'Configure when the message should be sent after the trigger conditions are met'
                  }
                </p>
              </div>

              <div className="space-y-6">
                <div className="space-y-4">
                  <Label>
                    {formData.trigger_type === 're_engagement' 
                      ? 'Send Time (before typical order time)'
                      : 'Time Delay'
                    }
                  </Label>
                  <div className="flex items-center space-x-4">
                    <Input
                      type="number"
                      value={formData.time_delay.value}
                      onChange={(e) => updateFormData({
                        time_delay: { ...formData.time_delay, value: parseInt(e.target.value) || 0 }
                      })}
                      className="w-24"
                      min="0"
                    />
                    <Select
                      value={formData.time_delay.unit}
                      onValueChange={(value) => updateFormData({
                        time_delay: { ...formData.time_delay, unit: value as TimeUnit }
                      })}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {formData.trigger_type === 're_engagement' 
                          ? (
                            // Only show minutes and hours for re_engagement
                            <>
                              <SelectItem value="minutes">Minutes</SelectItem>
                              <SelectItem value="hours">Hours</SelectItem>
                            </>
                          ) : (
                            // Show all options for other trigger types
                            Object.entries(TIME_UNIT_LABELS).map(([value, label]) => (
                              <SelectItem key={value} value={value}>
                                {label}
                              </SelectItem>
                            ))
                          )
                        }
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {getTimingMessage({
                      trigger_type: formData.trigger_type,
                      time_delay: formData.time_delay,
                      default_conditions: formData.conditions
                    })}
                  </p>
                  {errors.time_delay && (
                    <p className="text-sm text-destructive">{errors.time_delay}</p>
                  )}
                </div>

                <Separator />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="starts_at">Start Date (Optional)</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {startDate ? format(startDate, "PPP") : "Pick a date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={startDate}
                          onSelect={setStartDate}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <p className="text-xs text-muted-foreground">
                      Trigger will not activate before this date
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="expires_at">End Date (Optional)</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {endDate ? format(endDate, "PPP") : "Pick a date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={endDate}
                          onSelect={setEndDate}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <p className="text-xs text-muted-foreground">
                      Trigger will stop activating after this date
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {currentStep === 'review' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium mb-2">Review & Confirm</h3>
                <p className="text-sm text-muted-foreground mb-6">
                  Review your trigger configuration before saving
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Basic Info</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <dl className="space-y-2">
                      <div>
                        <dt className="text-sm font-medium">Name:</dt>
                        <dd className="text-sm text-muted-foreground">{formData.rule_name}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium">Type:</dt>
                        <dd className="text-sm text-muted-foreground">
                          {TRIGGER_TYPE_LABELS[formData.trigger_type]}
                        </dd>
                      </div>
                      {formData.rule_description && (
                        <div>
                          <dt className="text-sm font-medium">Description:</dt>
                          <dd className="text-sm text-muted-foreground">{formData.rule_description}</dd>
                        </div>
                      )}
                    </dl>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Timing</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <dl className="space-y-2">
                      <div>
                        <dt className="text-sm font-medium">
                          {formData.trigger_type === 're_engagement' ? 'Send Time:' : 'Delay:'}
                        </dt>
                        <dd className="text-sm text-muted-foreground">
                          {formData.trigger_type === 're_engagement'
                            ? (formData.time_delay.value === 0
                              ? 'At typical order time'
                              : `${formatTimeDelay(formData.time_delay)} before typical order time`
                            )
                            : (formData.time_delay.value === 0
                              ? 'Immediately'
                              : formatTimeDelay(formData.time_delay))
                          }
                        </dd>
                      </div>
                      {formData.starts_at && (
                        <div>
                          <dt className="text-sm font-medium">Starts:</dt>
                          <dd className="text-sm text-muted-foreground">
                            {new Date(formData.starts_at).toLocaleString()}
                          </dd>
                        </div>
                      )}
                      {formData.expires_at && (
                        <div>
                          <dt className="text-sm font-medium">Expires:</dt>
                          <dd className="text-sm text-muted-foreground">
                            {new Date(formData.expires_at).toLocaleString()}
                          </dd>
                        </div>
                      )}
                    </dl>
                  </CardContent>
                </Card>

                <Card className="md:col-span-2">
                  <CardHeader>
                    <CardTitle className="text-base">Status</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium">Initial Status</p>
                        <p className="text-sm text-muted-foreground">
                          {formData.is_active ? 'Active - Trigger will start processing immediately' : 'Paused - Trigger will be saved but not active'}
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Label htmlFor="is_active" className="text-sm">
                          {formData.is_active ? 'Active' : 'Paused'}
                        </Label>
                        <Switch
                          id="is_active"
                          checked={formData.is_active ?? false}
                          onCheckedChange={(checked) => updateFormData({ is_active: checked })}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="md:col-span-2">
                  <CardHeader>
                    <CardTitle className="text-base">Conditions</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {formData.conditions.map((condition, index) => (
                        <div key={condition.id} className="flex items-center space-x-2">
                          {index > 0 && (
                            <Badge variant="outline" className="text-xs">
                              {formData.logic}
                            </Badge>
                          )}
                          <span className="text-sm">
                            {condition.field} {condition.operator} {
                              Array.isArray(condition.value) 
                                ? condition.value.join(', ')
                                : typeof condition.value === 'object' && condition.value !== null && 'min' in condition.value && 'max' in condition.value
                                ? `${condition.value.min || 'min'} to ${condition.value.max || 'max'}`
                                : condition.value
                            }
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card className="md:col-span-2">
                  <CardHeader>
                    <CardTitle className="text-base">Message Preview</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="bg-gray-50 rounded p-4">
                      <p className="text-sm whitespace-pre-wrap">
                        {formData.message_template}
                      </p>
                      <p className="text-xs text-muted-foreground mt-2">
                        + Automatic opt-out link will be appended
                      </p>
                    </div>

                    <Separator />

                    <EnhancedMessageCharacterCount
                      message={formData.message_template}
                      organisationId={user?.organisationId}
                      includeOptOut={true}
                      showVariableList={true}
                    />
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          onClick={currentStep === 'basic' ? onCancel : handlePrevious}
        >
          {currentStep === 'basic' ? 'Cancel' : (
            <>
              <ChevronLeft className="h-4 w-4 mr-2" />
              Previous
            </>
          )}
        </Button>

        <div className="flex space-x-2">
          {currentStep === 'review' ? (
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? 'Saving...' : (triggerId ? 'Update Trigger' : 'Create Trigger')}
            </Button>
          ) : (
            <Button onClick={handleNext}>
              Next
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TriggerForm;